import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { generateQRCode } from "./fetchData";
import { showSuccess, showError } from "../../../../util/ShowStatus";

interface QRCodeModalProps {
  visible: boolean;
  onHide: () => void;
}

interface QRCodeData {
  qrCodeBase64: string;
  qrCodeLink: string;
  formUrl: string;
  isExisting: boolean;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ visible, onHide }) => {
  const toast = useRef<Toast>(null);
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [qrLoading, setQRLoading] = useState<boolean>(false);
  const [isShareAvailable, setIsShareAvailable] = useState<boolean>(false);

  const frontendFormUrl = `${window.location.origin}/public/observe-inform-report`;
  const logoUrl =
    "https://storage.amelio-group.com/amelio/amelioLogo/amelio-blue-img.png";

  useEffect(() => {
    if (visible && !qrCodeData) {
      generateQRCode(
        setQRCodeData,
        setQRLoading,
        toast,
        frontendFormUrl,
        logoUrl,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleDownload = () => {
    if (!qrCodeData?.qrCodeLink) return;

    const link = document.createElement("a");
    link.href = qrCodeData.qrCodeLink;
    link.download = `ObserveInformReport_QR_${new Date().toISOString().split("T")[0]}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess("QR Code downloaded successfully", toast);
  };

  const handleCopyLink = async (type?: string) => {
    if (!qrCodeData?.formUrl) return;
    if (type === "share" && !qrCodeData?.qrCodeLink) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(
          type === "share" ? qrCodeData.qrCodeLink : qrCodeData.formUrl,
        );
        showSuccess("Link copied to clipboard", toast);
      } else {
        const ta = document.createElement("textarea");
        ta.value =
          type === "share" ? qrCodeData.qrCodeLink : qrCodeData.formUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand("copy");
          showSuccess("Link copied to clipboard", toast);
        } catch {
          showError("Failed to copy link", toast);
        }
        document.body.removeChild(ta);
      }
    } catch (err) {
      showError("Failed to copy link", toast);
    }
  };

  const handleShare = async () => {
    if (!qrCodeData) return;

    // Web Share API requires secure context (HTTPS) or localhost
    const isSecureContext =
      window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // Check if Web Share API is available and we're in secure context
    if (navigator.share && isSecureContext) {
      try {
        // Try sharing with file first (works on some mobile browsers)
        if (navigator.canShare) {
          const response = await fetch(qrCodeData.qrCodeLink);
          const blob = await response.blob();
          const file = new File([blob], "ObserveInformReport_QR.png", {
            type: "image/png",
          });

          const shareData = {
            title: "Observe. Inform. Report. Form",
            text: "Scan this QR code or use the link to submit an Observe. Inform. Report.",
            url: qrCodeData.formUrl,
            files: [file],
          };

          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return;
          }
        }

        // Fallback: share without file (more widely supported on mobile)
        await navigator.share({
          title: "Observe. Inform. Report. Form",
          text: "Use this link to submit an Observe. Inform. Report.",
          url: qrCodeData.qrCodeLink,
        });
        return;
      } catch (err: any) {
        // User cancelled share - don't show error or copy
        if (err.name === "AbortError") {
          return;
        }
        // For other errors, fall through to copy link
        console.error("Share failed:", err);
      }
    }

    // Fallback: copy link and show success message
    setIsShareAvailable(true);
    handleCopyLink("share");
  };

  const handlePrint = () => {
    if (!qrCodeData?.qrCodeLink) return;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Observe. Inform. Report. QR Code</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
              }
              .container {
                text-align: center;
                padding: 20px;
              }
              h1 {
                color: #152846;
                margin-bottom: 20px;
              }
              img {
                max-width: 300px;
                height: auto;
              }
              p {
                color: #666;
                margin-top: 20px;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h1>Observe. Inform. Report.</h1>
              <img src="${qrCodeData.qrCodeLink}" alt="QR Code" />
              <p>Scan this QR code to submit a report</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const headerTemplate = () => (
    <div className="tw-flex tw-items-center tw-gap-2">
      <i className="pi pi-qrcode tw-text-xl tw-text-primary"></i>
      <span className="tw-font-semibold tw-text-lg">QR Code</span>
    </div>
  );

  //   const footerTemplate = () => (
  //     <div className="tw-flex tw-justify-end tw-gap-2">
  //       <Button
  //         label="Close"
  //         icon="pi pi-times"
  //         className="p-button-text"
  //         onClick={onHide}
  //       />
  //     </div>
  //   );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={onHide}
        header={headerTemplate}
        // footer={footerTemplate}
        draggable={false}
        modal
        className="tw-w-full tw-max-w-md"
        contentClassName="tw-p-0"
      >
        <div className="tw-p-4">
          {qrLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10">
              <ProgressSpinner style={{ width: "50px", height: "50px" }} />
              <p className="tw-mt-4 tw-text-gray-600">Fetching QR Code...</p>
            </div>
          ) : qrCodeData ? (
            <div className="tw-flex tw-flex-col tw-items-center">
              {/* QR Code Image */}
              <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-md tw-mb-4">
                <img
                  src={qrCodeData.qrCodeLink}
                  alt="QR Code"
                  className="tw-w-64 tw-h-64"
                />
              </div>

              {/* Info Text */}
              <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-4">
                Share this QR code with anyone to allow them to submit an
                Observe. Inform. Report. without logging in.
              </p>

              {/* Form URL */}
              <div className="tw-w-full tw-bg-gray-100 tw-rounded-lg tw-p-3 tw-mb-4">
                <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Form URL:</p>
                <p className="tw-text-sm tw-text-gray-800 tw-break-all">
                  {qrCodeData.formUrl}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-w-full">
                <Button
                  label="Download"
                  icon="pi pi-download"
                  className="p-button-outlined tw-text-sm tw-border tw-p-2 tw-text-[#ffffff] tw-bg-[#1d3764] hover:tw-bg-[#152846]"
                  onClick={handleDownload}
                />
                <Button
                  label="Copy Form Link"
                  icon="pi pi-copy"
                  className="p-button-outlined tw-text-sm tw-border tw-p-2 tw-text-[#ffffff] tw-bg-[#1d3764] hover:tw-bg-[#152846]"
                  onClick={() => handleCopyLink()}
                />
                <Button
                  label={`${isShareAvailable ? "Copy" : "Share"} QR Link`}
                  icon="pi pi-share-alt"
                  className="p-button-outlined tw-text-sm tw-border tw-p-2 tw-text-[#ffffff] tw-bg-[#1d3764] hover:tw-bg-[#152846]"
                  onClick={handleShare}
                />
                <Button
                  label="Print"
                  icon="pi pi-print"
                  className="p-button-outlined tw-text-sm tw-border tw-p-2 tw-text-[#ffffff] tw-bg-[#1d3764] hover:tw-bg-[#152846]"
                  onClick={handlePrint}
                />
              </div>

              {/* {qrCodeData.isExisting && (
                <p className="tw-text-xs tw-flex tw-items-center tw-text-gray-500 tw-mt-4 tw-text-center">
                  <i className="pi pi-info-circle tw-mr-1"></i>
                  <span>Using previously fetched QR code</span>
                </p>
              )} */}
            </div>
          ) : (
            <div className="tw-text-center tw-py-10">
              <i className="pi pi-exclamation-triangle tw-text-4xl tw-text-yellow-500 tw-mb-4"></i>
              <p className="tw-text-gray-600">Failed to fetch QR code</p>
              <Button
                label="Retry"
                icon="pi pi-refresh"
                className="tw-mt-4"
                onClick={() =>
                  generateQRCode(
                    setQRCodeData,
                    setQRLoading,
                    toast,
                    frontendFormUrl,
                    logoUrl,
                  )
                }
              />
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default QRCodeModal;
